import { useEffect, useEffectEvent, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { ArrowLeft, Save, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { DataLoader } from '@/components/ui/DataLoader';
import { buildMutationErrorHandler } from '@/utils/form-errors';
import {
  useInvestor,
  useInvestorContact,
  useCreateInvestorContact,
  useUpdateInvestorContact,
  useDeleteInvestorContact,
} from '@/hooks/react-query/useInvestors';
import { usePortalRoles } from '@/hooks/react-query/usePortalRoles';
import type {
  InvestorContactCreateData,
  InvestorContactSearchResult,
} from '@/types/investor';
import { InvestorContactFormFields } from './InvestorContactFormFields';
import { CopySourceNote } from './CopySourceNote';
import { ExistingContactPicker } from './ExistingContactPicker';
import {
  contactFormDataFromExisting,
  isEmailTaken,
} from './existingContactMapping';
import {
  EMPTY_CONTACT_FORM_DATA,
  validateContactForm,
  type InvestorContactFormErrors,
} from './investorContactFormValidation';

type FormErrors = InvestorContactFormErrors & { general?: string };

export default function InvestorContactDetailPage() {
  const navigate = useNavigate();
  const { investorId, contactId } = useParams();
  const isNew = !contactId || contactId === 'new';
  const [copySource, setCopySource] =
    useState<InvestorContactSearchResult | null>(null);

  const createMutation = useCreateInvestorContact();
  const updateMutation = useUpdateInvestorContact();
  const deleteMutation = useDeleteInvestorContact();

  // Pass empty contactId when deleting to disable the query and prevent 404 refetches
  const {
    data: contact,
    isLoading,
    error: fetchError,
  } = useInvestorContact(
    investorId || '',
    deleteMutation.isPending || deleteMutation.isSuccess ? '' : contactId || ''
  );

  const { data: portalRolesData } = usePortalRoles({ include_system: true });
  const portalRoles = portalRolesData?.items ?? [];

  const [errors, setErrors] = useState<FormErrors>({});
  // FormErrors is a closed shape (this codebase's forms otherwise use a
  // plain Record<string, string>); cast at this one seam, see form-errors.ts
  const setFormErrors = setErrors as (
    updater: (prev: Record<string, string>) => Record<string, string>
  ) => void;
  const [formData, setFormData] = useState<InvestorContactCreateData>(
    EMPTY_CONTACT_FORM_DATA
  );

  // Already cached when arriving from the investor's Contacts tab.
  const { data: investor } = useInvestor(isNew ? investorId : undefined);

  const [syncedContactId, setSyncedContactId] = useState<string | null>(null);
  if (!isNew && contact?.id && contact.id !== syncedContactId) {
    setSyncedContactId(contact.id);
    setFormData({
      first_name: contact.first_name,
      last_name: contact.last_name,
      email: contact.email || '',
      phone: contact.phone || '',
      mobile: contact.mobile || '',
      role: contact.role,
      title: contact.title || '',
      department: contact.department || '',
      is_primary: contact.is_primary,
      is_authorized_signatory: contact.is_authorized_signatory,
      receives_reports: contact.receives_reports,
      receives_capital_calls: contact.receives_capital_calls,
      receives_distributions: contact.receives_distributions,
      portal_role_id: contact.portal_role_id,
      address_line_1: contact.address_line_1 || '',
      address_line_2: contact.address_line_2 || '',
      city: contact.city || '',
      postcode: contact.postcode || '',
      country: contact.country || '',
    });
  }

  const backUrl = `/app/investors/${investorId}?tab=contacts`;

  const navigateBack = useEffectEvent(() => {
    navigate(backUrl);
  });

  // Navigate immediately when delete succeeds, without waiting for query invalidation
  useEffect(() => {
    if (deleteMutation.isSuccess) {
      navigateBack();
    }
  }, [deleteMutation.isSuccess]);

  const updateField = (
    field: keyof InvestorContactCreateData,
    value: string | boolean | null
  ) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field as keyof FormErrors]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  // A copy of someone already on this investor would clash on email, so they aren't offered.
  const isAlreadyOnInvestor = (candidate: InvestorContactSearchResult) =>
    candidate.investor_id === investorId ||
    isEmailTaken(investor?.contacts ?? [], candidate.email);

  const handleCopy = (source: InvestorContactSearchResult) => {
    setFormData(contactFormDataFromExisting(source));
    setCopySource(source);
    setErrors({});
  };

  const clearCopy = () => {
    setFormData(EMPTY_CONTACT_FORM_DATA);
    setCopySource(null);
    setErrors({});
  };

  const validate = (): boolean => {
    const newErrors = validateContactForm(formData);
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = () => {
    if (!validate() || !investorId) return;

    const options = {
      onSuccess: () => navigate(backUrl),
      onError: buildMutationErrorHandler(setFormErrors, {
        fallbackMessage: 'Failed to save contact',
      }),
    };

    if (isNew) {
      createMutation.mutate({ investorId, data: formData }, options);
    } else {
      updateMutation.mutate({ contactId: contactId!, data: formData }, options);
    }
  };

  const handleDelete = () => {
    if (!contactId) return;

    deleteMutation.mutate(contactId, {
      onError: buildMutationErrorHandler(setFormErrors, {
        fallbackMessage: 'Failed to delete contact',
      }),
    });
  };

  const saving = createMutation.isPending || updateMutation.isPending;

  return (
    <DataLoader
      data={isNew ? {} : contact}
      isLoading={!isNew && isLoading}
      error={deleteMutation.isSuccess ? null : fetchError}
    >
      {() => (
        <div className="mx-auto p-6 space-y-6">
          {/* Header */}
          <div className="flex flex-col gap-1">
            <button
              onClick={() => navigate(backUrl)}
              className="flex items-center gap-1.5 self-start text-sm text-gray-500 hover:text-gray-700 transition-colors"
            >
              <ArrowLeft className="h-3.5 w-3.5" />
              Back to Contacts
            </button>

            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <h1 className="text-2xl sm:text-3xl font-bold text-foreground">
                {isNew
                  ? 'New Contact'
                  : `${contact?.first_name || ''} ${contact?.last_name || ''}`}
              </h1>
              <div className="flex items-center gap-2">
                {!isNew && (
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button
                        variant="outline"
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      >
                        <Trash2 className="h-4 w-4 mr-2" />
                        Delete
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Delete Contact</AlertDialogTitle>
                        <AlertDialogDescription>
                          Are you sure you want to delete {contact?.first_name}{' '}
                          {contact?.last_name}? This action cannot be undone.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction
                          onClick={handleDelete}
                          className="bg-red-600 hover:bg-red-700"
                        >
                          Delete
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                )}
                <Button
                  onClick={handleSave}
                  disabled={saving}
                  className="flex items-center space-x-2"
                >
                  <Save className="h-4 w-4" />
                  <span>{saving ? 'Saving...' : 'Save Contact'}</span>
                </Button>
              </div>
            </div>
          </div>

          {/* Error Display */}
          {errors.general && (
            <div className="bg-red-50 border border-red-200 rounded-md p-4">
              <div className="text-red-700">{errors.general}</div>
            </div>
          )}

          {isNew && (
            <div className="space-y-2">
              <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                <div className="w-full sm:max-w-sm">
                  <ExistingContactPicker
                    onSelect={handleCopy}
                    isExcluded={isAlreadyOnInvestor}
                  />
                </div>
                <p className="text-sm text-muted-foreground">
                  or fill in the details below.
                </p>
              </div>
              {copySource && (
                <div className="flex items-start gap-2">
                  <CopySourceNote source={copySource} />
                  <Button
                    variant="link"
                    size="sm"
                    onClick={clearCopy}
                    className="h-auto p-0"
                  >
                    Clear
                  </Button>
                </div>
              )}
            </div>
          )}

          <InvestorContactFormFields
            formData={formData}
            errors={errors}
            onChange={updateField}
            portalRoles={portalRoles}
          />
        </div>
      )}
    </DataLoader>
  );
}
